import { ArrowRight } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import { AuditLink } from '@/components/audit/AuditLink'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { useTableColumns, type UseTableColumnsResult } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { RECORD_NOUN, breakablePath, type ChangeTone } from '@/lib/auditPresentation'
import { recordTarget } from '@/lib/auditLinks'
import { recordSource } from '@/lib/auditTrailSources'
import type { AuditCell, AuditView } from '@/lib/auditViews'
import { actorLabel } from '@/lib/storageAudit'
import { cn } from '@/lib/utils'
import type { AuditCenterRow } from '@/types/audit'

const TONE: Record<ChangeTone, string> = {
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  update: 'bg-blue-50 text-blue-700 border-blue-200',
  remove: 'bg-red-50 text-red-700 border-red-200',
  complete: 'bg-violet-50 text-violet-700 border-violet-200',
  access: 'bg-slate-50 text-slate-700 border-slate-200',
  stock: 'bg-amber-50 text-amber-800 border-amber-200',
}

const DASH = <span className='text-muted-foreground'>—</span>

/**
 * The record a row is about, with what kind of record it is: linked to its own
 * trail, or to its page when it has no trail (a template, a run, a pick list).
 */
function RecordCell({ row }: { row: AuditCenterRow }) {
  const { record } = row
  if (!record) return DASH
  const source = recordSource(record)
  return (
    <div className='break-words text-xs'>
      <AuditLink
        target={source?.openItem ?? recordTarget(record, row)}
        source={source}
        title={`Open the audit trail of ${record.label}`}
        className='font-medium'
      >
        {breakablePath(record.label)}
      </AuditLink>
      <div className='text-muted-foreground'>{RECORD_NOUN[record.kind]}</div>
    </div>
  )
}

function ActorCell({ row }: { row: AuditCenterRow }) {
  const named = Boolean(row.changedByActor?.name)
  return (
    <div className='min-w-0 break-words'>
      <div className={cn(!named && 'font-mono text-xs')} title={row.changedByActor?.email ?? row.changedBy}>
        {actorLabel(row)}
      </div>
      {named && row.changedByActor?.email && <div className='text-xs text-muted-foreground'>{row.changedByActor.email}</div>}
    </div>
  )
}

function Cell({ cell, row }: { cell: AuditCell; row: AuditCenterRow }): ReactNode {
  switch (cell.kind) {
    case 'datetime': {
      const at = new Date(row.changedAt)
      return (
        <time dateTime={row.changedAt} title={at.toLocaleString()} className='tabular-nums'>
          <div>{at.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</div>
          <div className='text-xs text-muted-foreground'>
            {at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </time>
      )
    }
    case 'actor':
      return <ActorCell row={row} />
    case 'record':
      return <RecordCell row={row} />
    case 'badge':
      return (
        <span className={cn('inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium', TONE[cell.tone])}>
          {cell.label}
        </span>
      )
    case 'text':
      if (!cell.value) return DASH
      return (
        <div className='min-w-0 break-words text-xs'>
          <div className={cn(cell.strong && 'font-medium text-sm')}>
            {cell.link ? (
              <AuditLink target={cell.link.target ?? cell.link.source?.openItem ?? null} source={cell.link.source ?? null}>
                {breakablePath(cell.value)}
              </AuditLink>
            ) : (
              breakablePath(cell.value)
            )}
          </div>
          {cell.sub && <div className='text-muted-foreground'>{breakablePath(cell.sub)}</div>}
        </div>
      )
    case 'lines':
      if (cell.lines.length === 0) return DASH
      return (
        <ul className='min-w-0 space-y-0.5 break-words text-xs'>
          {cell.lines.map((l, i) => (
            <li key={`${l.label ?? ''}-${i}`}>
              {l.label && <span className='text-muted-foreground'>{l.label}: </span>}
              <span className='font-medium'>{breakablePath(l.value)}</span>
            </li>
          ))}
        </ul>
      )
    case 'changes':
      if (cell.changes.length === 0) return DASH
      return (
        <ul className='min-w-0 space-y-0.5 break-words text-xs'>
          {cell.changes.map((c, i) => (
            <li key={`${c.label}-${i}`} className='flex flex-wrap items-center gap-x-1'>
              {c.label && <span className='text-muted-foreground'>{c.label}:</span>}
              {c.from !== undefined && (
                <>
                  <span>{breakablePath(c.from)}</span>
                  <ArrowRight className='h-3 w-3 text-muted-foreground' aria-label='changed to' />
                </>
              )}
              <span className='font-medium'>{breakablePath(c.to)}</span>
            </li>
          ))}
        </ul>
      )
  }
}

/**
 * A trail's columns in the app's standard table: each can be resized, hidden
 * and shown from the Columns menu in the header, and sorted from its header
 * when the server can order by it — the layout and the order saved per person
 * like every other table's. The Center holds it, since the order is part of
 * the search it sends.
 */
export function useAuditViewColumns(view: AuditView): UseTableColumnsResult {
  const defs = useMemo<TableColumnDef[]>(
    () =>
      view.columns.map((c) => ({
        id: c.id,
        header: c.header,
        defaultWidth: c.defaultWidth,
        defaultHidden: c.defaultHidden,
        // When it happened identifies the row: it is never hidden.
        locked: c.id === 'when',
        sortable: c.sortKey !== undefined,
      })),
    [view],
  )
  return useTableColumns(view.tableId, defs)
}

/**
 * One audit trail, in the layout that trail has of its own (auditViews): its
 * title and its columns (`useAuditViewColumns`).
 */
export function AuditTrailReport({
  view,
  columns: cols,
  rows,
  isLoading,
}: {
  view: AuditView
  columns: UseTableColumnsResult
  rows: AuditCenterRow[]
  isLoading: boolean
}) {
  const byId = useMemo(() => new Map(view.columns.map((c) => [c.id, c])), [view])

  return (
    <section aria-label={view.title} className='space-y-2'>
      <h2 className='text-base font-semibold'>{view.title}</h2>
      <div className='max-h-[40rem] overflow-auto rounded-lg border'>
        {/* Natural width, scrolling sideways, as the other wide tables do. */}
        <Table className='w-max min-w-full'>
          <TableHeader>
            <TableRow>
              {cols.visible.map(({ def }) => (
                <ResizableTableHead key={def.id} columnId={def.id} columns={cols} label={def.header} className='whitespace-nowrap'>
                  {def.header}
                </ResizableTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={cols.visible.length} className='py-6 text-center text-sm text-muted-foreground'>
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols.visible.length} className='py-6 text-center text-sm text-muted-foreground'>
                  No audit activity matches this search.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={`${row.trail}:${row.id}`}>
                  <ColumnCells
                    columns={cols}
                    cells={Object.fromEntries(
                      cols.visible.map(({ def }) => [
                        def.id,
                        <TableCell key={def.id} className='align-top whitespace-normal'>
                          {/* Wraps within the column's width — resized or default — so a
                              long value never stretches its column or runs under the next. */}
                          <div style={{ maxWidth: cols.widthOf(def.id) ?? def.defaultWidth }}>
                            <Cell cell={byId.get(def.id)!.cell(row)} row={row} />
                          </div>
                        </TableCell>,
                      ]),
                    )}
                  />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
